import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  getTeacherCohortAssignmentProgress,
  getTeacherCohortStudentProgressMatrix,
  type TeacherCohortAssignmentProgressRow,
  type TeacherCohortProgressMatrixAssignment,
  type TeacherCohortProgressMatrixCell,
  type TeacherCohortProgressMatrixStudent,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { formatProgressLabel } from "../student/assignmentProgressStatus";
import { CohortProgressMatrix } from "./CohortProgressMatrix";
import { countPlanItems, formatPlanItemCounts } from "./submissions/cohortProgressCounts";
import { teacherAssignmentReviewUrl } from "./submissions/submissionsAggregate";

export type CohortProgressView = "by-student" | "by-assignment";

export interface CohortStudentProgressSectionProps {
  readonly cohortId: string;
  readonly view: CohortProgressView;
  readonly onViewChange: (view: CohortProgressView) => void;
}

export function CohortStudentProgressSection({ cohortId, view, onViewChange }: CohortStudentProgressSectionProps) {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [loading, setLoading] = useState(true);
  const [assignmentQuery, setAssignmentQuery] = useState("");
  const [assignmentProgress, setAssignmentProgress] = useState<{
    rosterTotal: number;
    assignments: TeacherCohortAssignmentProgressRow[];
  } | null>(null);
  const [progressMatrix, setProgressMatrix] = useState<{
    assignments: TeacherCohortProgressMatrixAssignment[];
    students: TeacherCohortProgressMatrixStudent[];
    cells: TeacherCohortProgressMatrixCell[];
  } | null>(null);

  const reload = useCallback(async () => {
    if (!bearer || !cohortId) return;
    setLoading(true);
    try {
      const [prog, matrix] = await Promise.all([
        getTeacherCohortAssignmentProgress(bearer, cohortId).catch(() => null),
        getTeacherCohortStudentProgressMatrix(bearer, cohortId).catch(() => null),
      ]);
      setAssignmentProgress(prog ? { rosterTotal: prog.rosterTotal, assignments: prog.assignments } : null);
      setProgressMatrix(
        matrix
          ? { assignments: matrix.assignments, students: matrix.students, cells: matrix.cells }
          : null
      );
    } finally {
      setLoading(false);
    }
  }, [bearer, cohortId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const visibleAssignments = useMemo(() => {
    if (!assignmentProgress) return [];
    const q = assignmentQuery.trim().toLowerCase();
    if (!q) return assignmentProgress.assignments;
    return assignmentProgress.assignments.filter((a) => a.lessonTitle.toLowerCase().includes(q));
  }, [assignmentProgress, assignmentQuery]);

  const assignmentCounts = useMemo(() => countPlanItems(visibleAssignments), [visibleAssignments]);

  return (
    <section className="teacher-panel teacher-panel--flush cohort-student-progress-section">
      <div className="teacher-panel-head-row teacher-pad">
        <h2 className="submissions-chart-title" style={{ margin: 0 }}>
          Student progress
        </h2>
        <div className="cohort-progress-view-tabs" role="tablist" aria-label="Progress views">
          <button
            type="button"
            role="tab"
            className={`cohort-progress-view-tab${view === "by-student" ? " is-active" : ""}`}
            aria-selected={view === "by-student"}
            onClick={() => onViewChange("by-student")}
          >
            By student
          </button>
          <button
            type="button"
            role="tab"
            className={`cohort-progress-view-tab${view === "by-assignment" ? " is-active" : ""}`}
            aria-selected={view === "by-assignment"}
            onClick={() => onViewChange("by-assignment")}
          >
            By assignment
          </button>
        </div>
      </div>
      {loading ? (
        <p className="muted teacher-pad">Loading student progress…</p>
      ) : view === "by-student" ? (
        progressMatrix ? (
          <CohortProgressMatrix
            cohortId={cohortId}
            assignments={progressMatrix.assignments}
            students={progressMatrix.students}
            cells={progressMatrix.cells}
          />
        ) : (
          <p className="muted teacher-pad">
            Progress grid unavailable (students need submission attempts wired to workspaces).
          </p>
        )
      ) : assignmentProgress === null ? (
        <p className="muted teacher-pad">
          Progress summary unavailable (students need submission attempts wired to workspaces).
        </p>
      ) : assignmentProgress.assignments.length === 0 ? (
        <p className="muted teacher-pad">
          No assignments on the cohort plan yet.
          {assignmentProgress.rosterTotal > 0 ? ` Active roster ${assignmentProgress.rosterTotal}.` : ""}
        </p>
      ) : (
        <>
          <div className="cohort-progress-matrix-toolbar teacher-pad" style={{ paddingBottom: 0 }}>
            <label className="field cohort-progress-matrix-filter">
              <span className="field-label">Find assignment</span>
              <input
                className="input"
                type="search"
                value={assignmentQuery}
                onChange={(ev) => setAssignmentQuery(ev.target.value)}
                placeholder="Lesson or quiz title"
              />
            </label>
          </div>
          <p className="muted small teacher-pad" style={{ paddingTop: 0, marginTop: 0 }}>
            {assignmentProgress.rosterTotal} student{assignmentProgress.rosterTotal === 1 ? "" : "s"} on roster ·{" "}
            {formatPlanItemCounts(assignmentCounts)}
            {assignmentQuery.trim() ? " · filter on" : ""}. Lesson workspaces and quiz attempts.
          </p>
          <div className="teacher-table-wrap">
            <table className="teacher-table">
              <thead>
                <tr>
                  <th>Assignment</th>
                  <th>Type</th>
                  <th>{formatProgressLabel("not_started")}</th>
                  <th>{formatProgressLabel("in_progress")}</th>
                  <th>{formatProgressLabel("needs_help")}</th>
                  <th>{formatProgressLabel("complete")}</th>
                  <th>Complete rate</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visibleAssignments.map((row) => {
                  const total = row.notStarted + row.inProgress + row.needsHelp + row.complete;
                  const completeRate = total > 0 ? Math.round((row.complete / total) * 100) : null;
                  const isQuiz = row.itemKind === "quiz";
                  const reviewUrl = teacherAssignmentReviewUrl(cohortId, row);
                  return (
                    <tr key={row.assignmentId}>
                      <td>
                        <strong>{row.lessonTitle}</strong>
                      </td>
                      <td className="muted small">{isQuiz ? "Quiz" : "Lesson"}</td>
                      <td>{row.notStarted}</td>
                      <td>{row.inProgress}</td>
                      <td>{isQuiz ? "—" : row.needsHelp}</td>
                      <td>{row.complete}</td>
                      <td>{completeRate === null ? "-" : `${completeRate}%`}</td>
                      <td className="teacher-table-actions">
                        {reviewUrl ? (
                          <Link to={reviewUrl} className="btn secondary tight">
                            {isQuiz ? "View results" : "Review"}
                          </Link>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
                {visibleAssignments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="muted small">
                      No assignments match your search.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
